/**
 * Hub Profile management for luckyanimal-nawabari.
 *
 * Hub (GET /profiles/me) is the source of truth.
 * In-memory cache only — NO localStorage.
 *
 * Initialization flow:
 *   1. syncHubProfile() — read-only fetch, does NOT call syncProfile()
 *   2. Cache in memory
 *   3. Render in UI
 *
 * Per .claude/rules/ymg-implementation-checklist.md, we do NOT call syncProfile()
 * at init time — the server auto-generates an anonymous default profile for
 * first-time guests and syncProfile() would overwrite it with a client-side name.
 *
 * エラーポリシー（Sweeper `ymgProfile.ts` 準拠 — fail-closed）:
 *   - 認証エラー (ApiError.status === 401) は `notifyReauthRequired` で通知し
 *     呼び出し元に throw で伝播する。UI は再ログイン画面に倒す。
 *   - ネットワーク / 5xx も throw する。nawabari は localStorage を使わない
 *     設計のため軟着陸の受け皿がなく、userId 未取得のまま進むとマルチプレイ
 *     ロビーで対戦開始不可になるなどの実害が出るため、黙って anonymous
 *     default を返さずエラー画面に倒す方が安全（antipatterns.md §1 準拠）。
 *   - 「サイレントフォールバック禁止」ルールに従い、`// fallback:ok` を
 *     付けない経路は全て throw で伝播させる。
 *
 * ymgSyncProfile() / ymgGetAvatarIcons() are provided for EXPLICIT user edits
 * (e.g. ProfileEditModal). They throw on failure per no-silent-fallback rule.
 */

import {
  getYmgSdk,
  getGameId,
  ymgFetch,
  isSdkAuthError,
  hasCapacitorSessionToken,
  isLocalDevBypass,
} from './sdk'
import { getAuthState } from './auth'
import { AUTH_PREF_KEYS } from '../config/auth'
import { ApiError, notifyReauthRequired } from './ymgAuth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LocalProfile {
  /**
   * Hub に登録された自分の userId。T15 リアルタイム対戦で自分/相手を識別するために必要。
   * anonymous default を含めて getMyProfile() のレスポンスは userId を返すため、
   * /profiles/me フェッチ成功時は必ず値が入る。フェッチ失敗時のフォールバックでは null。
   */
  userId: string | null
  displayName: string
  pictureUrl: string | null
  avatarIconId: string | null
}

const DEFAULT_DISPLAY_NAME = 'プレイヤー'
const LOCAL_DEV_PROFILE: HubProfile = {
  userId: 'local-dev-user',
  displayName: DEFAULT_DISPLAY_NAME,
  pictureUrl: null,
  avatarIconId: null,
  emojiAvatar: null,
}

// ---------------------------------------------------------------------------
// State (in-memory only)
// ---------------------------------------------------------------------------

let currentProfile: LocalProfile | null = null
let avatarIconsCache: AvatarIcon[] | null = null

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapHubProfile(hp: HubProfile): LocalProfile {
  return {
    userId: hp.userId || null,
    displayName: hp.displayName || DEFAULT_DISPLAY_NAME,
    pictureUrl: hp.pictureUrl || null,
    avatarIconId: hp.avatarIconId || null,
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Return the cached profile (null if not yet fetched). */
export function getProfile(): LocalProfile | null {
  return currentProfile
}

/**
 * Fetch the current user's Hub profile (read-only).
 *
 * 成功時: Hub のプロフィールをキャッシュして返す。
 * 認証エラー (401) 時: `notifyReauthRequired` で UI に通知し throw。
 * ネットワーク / 5xx / その他 API エラー: throw（呼び出し元が起動エラー画面へ倒す）。
 *
 * 以前の anonymous default フォールバックは削除した。
 * 理由: nawabari は in-memory cache のみで localStorage 源泉がなく、
 *       userId 未取得のままタイトル画面へ進むとマルチプレイ対戦が開始不可になる。
 *       黙って既定値を返すと本番で 401 に気付けない（antipatterns.md §1 違反）。
 */
export async function syncHubProfile(): Promise<LocalProfile> {
  const sdk = await getYmgSdk()
  const gameId = getGameId()
  let hubProfile: HubProfile | null = null
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  // 試行順（signed_in か否かで前後を入れ替える）:
  //   preferFetch=true  : fetch → SDK fallback
  //   preferFetch=false : SDK → fetch fallback
  //
  // いずれの経路でも 401 は即座に再認証通知に倒し、ネットワーク/5xx も throw する。
  // SDK 経路で認証エラー以外が出た場合に限り、fetch フォールバックを試みる（CDN SDK
  // のバージョン差異で単に実装が古い可能性があるため）。

  async function tryFetch(): Promise<void> {
    try {
      const res = await ymgFetch<{ ok: boolean; profile: HubProfile }>(
        '/profiles/me',
        { method: 'GET', needsGameIdHeader: true },
      )
      hubProfile = res?.profile ?? null
    } catch (err) {
      if (err instanceof ApiError && err.status === 401 && isLocalDevBypass()) {
        console.warn('[HubProfile] Using local dev profile after /profiles/me 401')
        hubProfile = LOCAL_DEV_PROFILE
        return
      }
      // ymgFetch は既に ApiError を組み立てて notifyReauthRequired まで済ませている。
      // ここでは握り潰さず、そのまま呼び出し元へ伝播させる。
      throw err
    }
  }

  async function trySdk(): Promise<void> {
    if (typeof sdk.api?.getMyProfile !== 'function') return
    try {
      const res = await sdk.api.getMyProfile({ gameId })
      // SDK が「直近にキャッシュした profile」を返してきた場合は採用しない。
      // 連携・復旧直後に古い displayName が返るのを避けるため、fetch fallback
      // で Hub から fresh data を取り直させる（Sweeper `ymgGetMyProfile` 準拠）。
      const cached = (res as unknown as { cached?: boolean })?.cached === true
      if (cached) return
      hubProfile = res?.profile ?? null
    } catch (err) {
      // SDK からの 401 は ApiError に正規化して再認証通知に倒す。
      // 非認証系エラー（ネットワーク断・CDN バージョン差異・5xx 等）は fetch
      // フォールバックで再試行できる可能性があるので、warn だけ残して次へ。
      if (isSdkAuthError(err)) {
        if (isLocalDevBypass()) {
          console.warn('[HubProfile] Using local dev profile after SDK profile auth error')
          hubProfile = LOCAL_DEV_PROFILE
          return
        }
        const error = new ApiError(
          401,
          { message: 'SDK authentication failed' },
          undefined,
          true,
        )
        notifyReauthRequired(error)
        throw error
      }
      console.warn(
        '[HubProfile] sdk.api.getMyProfile failed (non-auth), trying fetch fallback:',
        err,
      )
    }
  }

  if (preferFetch) {
    await tryFetch()
    if (!hubProfile) await trySdk()
  } else {
    await trySdk()
    if (!hubProfile) await tryFetch()
  }

  if (!hubProfile) {
    // 両経路が「例外ではなく profile=null」を返したケース。サイレントに既定値へ
    // 倒すのを避けるため ApiError として明示的に失敗させる。
    throw new ApiError(
      500,
      { message: 'Hub returned no profile' },
      undefined,
      false,
    )
  }

  currentProfile = mapHubProfile(hubProfile)
  persistSupportUid(currentProfile.userId)
  console.log('[HubProfile] Loaded:', currentProfile.displayName)
  return currentProfile
}

/** Clear the in-memory profile cache (e.g. on logout). */
export function clearProfileCache(): void {
  currentProfile = null
  avatarIconsCache = null
  persistSupportUid(null)
}

function persistSupportUid(userId: string | null): void {
  if (typeof localStorage === 'undefined') return
  if (userId) {
    localStorage.setItem(AUTH_PREF_KEYS.uid, userId)
  } else {
    localStorage.removeItem(AUTH_PREF_KEYS.uid)
  }
}

// ---------------------------------------------------------------------------
// Avatar icons (GET /avatar-icons, no auth required)
// ---------------------------------------------------------------------------

/**
 * Fetch available avatar icons from the Hub.
 *
 * - Endpoint: GET /avatar-icons (no authentication required)
 * - Cached in memory for the session (cleared via clearProfileCache())
 * - Throws on failure (no silent fallback per .claude/rules/no-silent-fallback.md)
 */
export async function ymgGetAvatarIcons(): Promise<AvatarIcon[]> {
  if (avatarIconsCache) return avatarIconsCache

  const res = await ymgFetch<{ ok: boolean; icons: AvatarIcon[] }>(
    '/avatar-icons',
    { method: 'GET' },
  )
  const icons = (res?.icons ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
  avatarIconsCache = icons
  return icons
}

// ---------------------------------------------------------------------------
// Sync profile (explicit user edit via ProfileEditModal)
// ---------------------------------------------------------------------------

export interface SyncProfileParams {
  displayName: string
  avatarIconId?: string | null
}

/**
 * Update the user's Hub profile.
 *
 * - Endpoint: POST /profiles/me (X-Game-Id header required)
 * - Prefers SDK method (sdk.api.syncProfile), falls back to direct fetch if missing.
 * - Throws on failure — the caller (ProfileEditModal) surfaces an error and
 *   allows retry. See .claude/rules/no-silent-fallback.md.
 *
 * On success, the in-memory cache is updated and the fresh LocalProfile is returned.
 */
export async function ymgSyncProfile(
  params: SyncProfileParams,
): Promise<LocalProfile> {
  const sdk = await getYmgSdk()
  const gameId = getGameId()

  // avatarIconId が指定されていれば送信、undefined/null は明示的に null として送る
  // (サーバー側は null 受領でアイコン解除、未指定で現状維持)
  const body = {
    displayName: params.displayName,
    avatarIconId: params.avatarIconId ?? null,
  }

  let hubProfile: HubProfile | null = null

  // 1st: SDK method
  if (typeof sdk.api?.syncProfile === 'function') {
    try {
      const res = await sdk.api.syncProfile({ gameId, ...body })
      hubProfile = res?.profile ?? null
    } catch (err) {
      // 401 は fetch fallback でも同じ結果になるので、ここで再認証 UI に倒す。
      // ProfileEditModal が「保存できません」とだけ表示する状態を防ぐ
      // （syncHubProfile.trySdk と同じパターン）。
      if (isSdkAuthError(err)) {
        const error = new ApiError(
          401,
          { message: 'SDK syncProfile authentication failed' },
          undefined,
          true,
        )
        notifyReauthRequired(error)
        throw error
      }
      // 非認証系エラーは fetch フォールバックを試みる
      // (CDN SDK のバージョン差異で実装が欠落している可能性があるため)
      console.warn(
        '[HubProfile] sdk.api.syncProfile failed (non-auth), trying fetch fallback:',
        err,
      )
    }
  }

  // 2nd: fetch fallback
  if (!hubProfile) {
    const res = await ymgFetch<{ ok: boolean; profile: HubProfile }>(
      '/profiles/me',
      {
        method: 'POST',
        needsGameIdHeader: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    )
    hubProfile = res?.profile ?? null
  }

  if (!hubProfile) {
    // ここに到達する = 両方成功したが profile が null
    // サイレントに成功扱いせず、明示的に失敗を伝える
    throw new Error('[HubProfile] syncProfile returned no profile')
  }

  currentProfile = mapHubProfile(hubProfile)
  return currentProfile
}
